import React from "react";
import { FixedBundleField, ConfigurableBundleField } from "@codway-dev/ui";
import { mapBundleResolvedDataToFieldProps } from "@codway-dev/shared";
import { useShopCurrency } from "../../../hooks/useShopCurrency";
import type { UpsellEmbedPreviewProps } from "./types";

// Hoisted so every render passes the same references. The Form Editor
// re-renders on every keystroke in adjacent fields, and inline `{}` /
// `() => {}` props would force the UI Kit to re-render the (disabled)
// bundle field on each one.
const EMPTY_SELECTIONS: Record<string, string[]> = {};
const NOOP = () => {};

// Form-editor preview: renders the bundle field in disabled/read-only
// mode so the merchant sees how the offer will look inside the COD form
// without being able to interact with it. The Form Editor exposes no
// per-field customization for bundle embeds (the dedicated bundle editor
// handles that) — `FieldSettingsPanel` shows a link-out card instead.
export const BundleEmbedPreview: React.FC<UpsellEmbedPreviewProps> = ({
  resolvedData,
}) => {
  const { formatPrice } = useShopCurrency();
  if (!resolvedData) return null;

  const fieldProps = mapBundleResolvedDataToFieldProps(
    resolvedData,
    formatPrice
  );
  if (!fieldProps) return null;

  if (fieldProps.kind === "fixed") {
    return (
      <FixedBundleField
        items={fieldProps.items}
        title={fieldProps.title}
        description={fieldProps.description}
        imageUrl={fieldProps.imageUrl}
        totalPriceLabel={fieldProps.totalPriceLabel}
        compareAtLabel={fieldProps.compareAtLabel}
        savingsLabel={fieldProps.savingsLabel}
        styles={fieldProps.styles}
        selected={false}
        onToggle={NOOP}
        disabled
      />
    );
  }

  return (
    <ConfigurableBundleField
      groups={fieldProps.groups}
      title={fieldProps.title}
      description={fieldProps.description}
      imageUrl={fieldProps.imageUrl}
      totalPriceLabel={fieldProps.totalPriceLabel}
      compareAtLabel={fieldProps.compareAtLabel}
      savingsLabel={fieldProps.savingsLabel}
      styles={fieldProps.styles}
      selections={EMPTY_SELECTIONS}
      onSelectionChange={NOOP}
      mode="picker"
      onAddToCart={NOOP}
      onEdit={NOOP}
      onRemove={NOOP}
      disabled
    />
  );
};
